import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "wouter";
import {
  Check,
  ChevronDown,
  ChevronUp,
  HeartPulse,
  Loader2,
  Package,
  RotateCcw,
  Sparkles,
  UserCircle,
  Users,
  Video,
} from "lucide-react";
import type { Persona, PersonaCharacter, Product } from "@shared/schema";
import { getQueryFn } from "@/lib/queryClient";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

type PainPillar = {
  id: string;
  name: string;
  description?: string | null;
  status?: string | null;
  archivedAt?: string | null;
};

type PickerOption = {
  value: string;
  label: string;
  description?: string | null;
  imageUrl?: string | null;
};

const DEFAULT_GRWM_OCCASION = "Morning · Post-Shower";
const GRWM_OCCASION_OPTIONS = [
  DEFAULT_GRWM_OCCASION,
  "Morning · Getting Ready to Leave",
  "Workday Preparation",
  "Getting Ready to Go Out",
  "Travel Morning",
  "Bedtime Routine",
  "Custom",
] as const;

const DEFAULT_CREATIVE_TWIST = "None";
const CREATIVE_TWIST_OPTIONS = [
  DEFAULT_CREATIVE_TWIST,
  "Running Late",
  "Cold-Weather Morning",
  "Packing for a Trip",
  "Date-Night Preparation",
  "Post-Workout Routine",
  "Bedtime Wind-Down",
  "Custom",
] as const;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";
}

function isActiveRecord(record: { status?: string | null; archivedAt?: unknown }): boolean {
  return record.status !== "archived" && !record.archivedAt;
}

function Picker({
  icon,
  label,
  placeholder,
  value,
  onChange,
  options,
  disabled,
  loading,
  error,
  emptyMessage,
  testId,
  required = true,
}: {
  icon: React.ReactNode;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  options: PickerOption[];
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  emptyMessage: string;
  testId: string;
  required?: boolean;
}) {
  const selected = options.find((option) => option.value === value);
  const hasOptions = options.length > 0;

  return (
    <div className="min-w-0 space-y-2">
      <Label htmlFor={testId} className="flex items-center gap-2 text-sm font-medium">
        <span className="text-muted-foreground">{icon}</span>
        {label}
        {required && <span className="text-destructive" aria-hidden="true">*</span>}
      </Label>
      <Select value={value} onValueChange={onChange} disabled={disabled || loading || !hasOptions}>
        <SelectTrigger
          id={testId}
          data-testid={testId}
          aria-label={label}
          aria-invalid={!!error}
          aria-describedby={error ? `${testId}-error` : undefined}
          className="h-auto min-h-12 w-full"
        >
          {loading ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading {label.toLowerCase()}…
            </span>
          ) : selected ? (
            <span className="flex min-w-0 items-center gap-2 text-left">
              {selected.imageUrl ? (
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarImage src={selected.imageUrl} alt="" />
                  <AvatarFallback>{initials(selected.label)}</AvatarFallback>
                </Avatar>
              ) : null}
              <span className="min-w-0">
                <span className="block truncate font-medium">{selected.label}</span>
                {selected.description && (
                  <span className="block truncate text-xs text-muted-foreground">{selected.description}</span>
                )}
              </span>
            </span>
          ) : (
            <SelectValue placeholder={placeholder} />
          )}
        </SelectTrigger>
        <SelectContent className="max-w-[min(32rem,calc(100vw-2rem))]">
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} className="py-2">
              <span className="flex min-w-0 items-center gap-2">
                {option.imageUrl ? (
                  <Avatar className="h-7 w-7 shrink-0">
                    <AvatarImage src={option.imageUrl} alt="" />
                    <AvatarFallback>{initials(option.label)}</AvatarFallback>
                  </Avatar>
                ) : null}
                <span className="min-w-0">
                  <span className="block truncate">{option.label}</span>
                  {option.description && (
                    <span className="block max-w-[24rem] truncate text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  )}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!loading && !hasOptions && <p className="text-xs text-muted-foreground">{emptyMessage}</p>}
      {error && (
        <p id={`${testId}-error`} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function QueryError({ message }: { message: string }) {
  return (
    <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      {message}
    </div>
  );
}

export default function GrwmugcPbPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [painPillarId, setPainPillarId] = useState("");
  const [personaId, setPersonaId] = useState("");
  const [characterId, setCharacterId] = useState("");
  const [productId, setProductId] = useState("");
  const [creativeStrategy, setCreativeStrategy] = useState("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isReviewed, setIsReviewed] = useState(false);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const [grwmOccasion, setGrwmOccasion] = useState(DEFAULT_GRWM_OCCASION);
  const [customGrwmOccasion, setCustomGrwmOccasion] = useState("");
  const [creativeTwist, setCreativeTwist] = useState(DEFAULT_CREATIVE_TWIST);
  const [customCreativeTwist, setCustomCreativeTwist] = useState("");
  const [specificSetting, setSpecificSetting] = useState("");
  const [openingAction, setOpeningAction] = useState("");
  const [applicationOrder, setApplicationOrder] = useState("");
  const [closingActivity, setClosingActivity] = useState("");
  const [conceptsToAvoid, setConceptsToAvoid] = useState("");

  const painPillarsKey = `/api/brands/${brandId}/pain-pillars?status=active`;
  const personasKey = `/api/brands/${brandId}/personas`;
  const productsKey = `/api/brands/${brandId}/products`;

  const painPillarsQuery = useQuery<PainPillar[]>({
    queryKey: [painPillarsKey],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!brandId,
    retry: 2,
    staleTime: 30_000,
  });
  const personasQuery = useQuery<Persona[]>({
    queryKey: [personasKey],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!brandId,
    retry: 2,
    staleTime: 30_000,
  });
  const productsQuery = useQuery<Product[]>({
    queryKey: [productsKey],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!brandId,
    retry: 2,
    staleTime: 30_000,
  });
  const charactersQuery = useQuery<PersonaCharacter[]>({
    queryKey: [`/api/personas/${personaId}/characters`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!personaId,
    retry: 2,
    staleTime: 30_000,
  });

  const painPillars = useMemo(
    () => (painPillarsQuery.data || []).filter(isActiveRecord),
    [painPillarsQuery.data],
  );
  const personas = useMemo(
    () => (personasQuery.data || []).filter(isActiveRecord),
    [personasQuery.data],
  );
  const products = useMemo(
    () => (productsQuery.data || []).filter(isActiveRecord),
    [productsQuery.data],
  );
  const characters = charactersQuery.data || [];

  const selectedPersona = personas.find((persona) => persona.id === personaId);
  const selectedCharacter = characters.find((character) => character.id === characterId);
  const selectedPillar = painPillars.find((pillar) => pillar.id === painPillarId);
  const selectedProduct = products.find((product) => product.id === productId);
  const activeAdvancedOverrideCount = [
    grwmOccasion !== DEFAULT_GRWM_OCCASION,
    customGrwmOccasion.trim().length > 0,
    creativeTwist !== DEFAULT_CREATIVE_TWIST,
    customCreativeTwist.trim().length > 0,
    specificSetting.trim().length > 0,
    openingAction.trim().length > 0,
    applicationOrder.trim().length > 0,
    closingActivity.trim().length > 0,
    conceptsToAvoid.trim().length > 0,
  ].filter(Boolean).length;

  // A persona change is the only input that changes the model's valid domain.
  // Keep the reset in an effect so it also covers a persona disappearing from
  // a refreshed response.
  useEffect(() => {
    setCharacterId("");
    setValidationErrors((current) => {
      if (!current.character) return current;
      const next = { ...current };
      delete next.character;
      return next;
    });
  }, [personaId]);

  useEffect(() => {
    if (characterId && !characters.some((character) => character.id === characterId)) {
      setCharacterId("");
    }
  }, [characterId, characters]);

  const pillarOptions: PickerOption[] = painPillars.map((pillar) => ({
    value: pillar.id,
    label: pillar.name,
    description: pillar.description,
  }));
  const personaOptions: PickerOption[] = personas.map((persona) => ({
    value: persona.id,
    label: persona.name,
    description: persona.summary || persona.awarenessStage?.replace(/_/g, " "),
    imageUrl: persona.imageUrl,
  }));
  const characterOptions: PickerOption[] = characters.map((character) => ({
    value: character.id,
    label: character.name,
    description: [
      character.age ? `Age ${character.age}` : null,
      character.genderPresentation,
      character.ethnicityHeritage,
      character.regionOrSetting,
    ].filter(Boolean).join(" · ") || character.imagePromptSummary,
    imageUrl: character.avatarUrl,
  }));
  const productOptions: PickerOption[] = products.map((product) => ({
    value: product.id,
    label: product.name,
    description: product.category || product.summary || product.description,
    imageUrl: Array.isArray(product.adgenImageRefs) && typeof product.adgenImageRefs[0] === "string"
      ? product.adgenImageRefs[0]
      : null,
  }));

  const setField = (field: string, setter: (value: string) => void, value: string) => {
    setter(value);
    setIsReviewed(false);
    setValidationErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!painPillarId) errors.painPillar = "Choose an active Pain Pillar.";
    if (!personaId) errors.persona = "Choose a brand persona.";
    if (!personaId) {
      errors.character = "Choose a persona before selecting a model.";
    } else if (charactersQuery.isError) {
      errors.character = "Models could not be loaded. Try again before continuing.";
    } else if (characters.length === 0) {
      errors.character = `No models are available for ${selectedPersona?.name || "this persona"}.`;
    } else if (!characterId) {
      errors.character = "Choose a model for this setup.";
    }
    if (!productId) errors.product = "Choose a brand product.";
    if (!creativeStrategy.trim()) errors.strategy = "Describe the creative strategy.";
    setValidationErrors(errors);
    setIsReviewed(Object.keys(errors).length === 0);
    return Object.keys(errors).length === 0;
  };

  const resetAdvancedSettings = () => {
    setGrwmOccasion(DEFAULT_GRWM_OCCASION);
    setCustomGrwmOccasion("");
    setCreativeTwist(DEFAULT_CREATIVE_TWIST);
    setCustomCreativeTwist("");
    setSpecificSetting("");
    setOpeningAction("");
    setApplicationOrder("");
    setClosingActivity("");
    setConceptsToAvoid("");
  };

  const dataErrors = [
    painPillarsQuery.isError ? "Pain Pillars could not be loaded." : null,
    personasQuery.isError ? "Personas could not be loaded." : null,
    productsQuery.isError ? "Products could not be loaded." : null,
  ].filter(Boolean) as string[];
  const anyLoading = painPillarsQuery.isLoading || personasQuery.isLoading || productsQuery.isLoading;
  const hasNoEligibleInputs = !anyLoading && painPillars.length === 0 && personas.length === 0 && products.length === 0;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 p-4 sm:p-6 lg:p-8">
      <header className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Video className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-page-title">GRWMUGC PB</h1>
            <Badge variant="secondary">Input setup</Badge>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Choose the creative context for a Get Ready with Me UGC video. This first step only prepares your inputs; it does not generate a video.
          </p>
        </div>
      </header>

      {dataErrors.length > 0 && (
        <div className="space-y-2">
          {dataErrors.map((message) => <QueryError key={message} message={message} />)}
        </div>
      )}

      {hasNoEligibleInputs && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center p-8 text-center sm:p-10">
            <div className="rounded-full bg-primary/10 p-3 text-primary">
              <Sparkles className="h-6 w-6" aria-hidden="true" />
            </div>
            <h2 className="mt-4 text-lg font-semibold">Add brand context to get started</h2>
            <p className="mt-2 max-w-lg text-sm text-muted-foreground">
              This brand needs at least one active Pain Pillar, persona, and product before a GRWMUGC PB setup can be completed.
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="border-border/60">
        <CardContent className="space-y-6 p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-base font-semibold">Set your creative context</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                All choices are kept on this page until the generation flow is available.
              </p>
            </div>
          </div>

          {anyLoading ? (
            <div className="grid gap-5 md:grid-cols-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2">
              <Picker
                icon={<HeartPulse className="h-4 w-4" aria-hidden="true" />}
                label="Pain Pillar"
                placeholder={painPillars.length ? "Choose an active Pain Pillar" : "No active Pain Pillars"}
                value={painPillarId}
                onChange={(value) => setField("painPillar", setPainPillarId, value)}
                options={pillarOptions}
                emptyMessage="No active Pain Pillars are available for this brand."
                testId="select-grwmugc-pb-pain-pillar"
                error={validationErrors.painPillar}
              />
              <Picker
                icon={<Users className="h-4 w-4" aria-hidden="true" />}
                label="Brand persona"
                placeholder={personas.length ? "Choose a persona" : "No active personas"}
                value={personaId}
                onChange={(value) => setField("persona", setPersonaId, value)}
                options={personaOptions}
                emptyMessage="No active personas are available for this brand."
                testId="select-grwmugc-pb-persona"
                error={validationErrors.persona}
              />
              <Picker
                icon={<UserCircle className="h-4 w-4" aria-hidden="true" />}
                label="Model / character"
                placeholder={
                  !personaId
                    ? "Choose a persona first"
                    : charactersQuery.isLoading
                      ? "Loading models…"
                      : characters.length
                        ? "Choose a model"
                        : "No models for this persona"
                }
                value={characterId}
                onChange={(value) => setField("character", setCharacterId, value)}
                options={characterOptions}
                disabled={!personaId || charactersQuery.isError || characters.length === 0}
                loading={charactersQuery.isLoading}
                emptyMessage={
                  !personaId
                    ? "Select a persona to load its models."
                    : charactersQuery.isError
                      ? "Models could not be loaded. Try again."
                      : `No characters are available for ${selectedPersona?.name || "this persona"}.`
                }
                testId="select-grwmugc-pb-model"
                error={validationErrors.character}
              />
              <Picker
                icon={<Package className="h-4 w-4" aria-hidden="true" />}
                label="Brand product"
                placeholder={products.length ? "Choose a product" : "No active products"}
                value={productId}
                onChange={(value) => setField("product", setProductId, value)}
                options={productOptions}
                emptyMessage="No active products are available for this brand."
                testId="select-grwmugc-pb-product"
                error={validationErrors.product}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="grwmugc-pb-strategy" className="flex items-center gap-2 text-sm font-medium">
              <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              Creative strategy
              <span className="text-destructive" aria-hidden="true">*</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Describe the idea, tone, hook, or behavior the future video should communicate.
            </p>
            <Textarea
              id="grwmugc-pb-strategy"
              value={creativeStrategy}
              onChange={(event) => setField("strategy", setCreativeStrategy, event.target.value)}
              placeholder="e.g. A casual morning routine that shows how the product fits into a busy getting-ready moment."
              rows={5}
              maxLength={2000}
              aria-invalid={!!validationErrors.strategy}
              aria-describedby={validationErrors.strategy ? "grwmugc-pb-strategy-error" : undefined}
              data-testid="textarea-grwmugc-pb-strategy"
              className="resize-y"
            />
            <div className="flex items-start justify-between gap-3">
              {validationErrors.strategy ? (
                <p id="grwmugc-pb-strategy-error" role="alert" className="text-sm text-destructive">
                  {validationErrors.strategy}
                </p>
              ) : (
                <span />
              )}
              <span className="text-xs text-muted-foreground">{creativeStrategy.length}/2000</span>
            </div>
          </div>

          <Collapsible open={advancedSettingsOpen} onOpenChange={setAdvancedSettingsOpen}>
            <div className="border-t border-dashed border-border/60 pt-5">
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto min-h-0 w-full justify-start gap-2 px-2 py-2 text-left hover:bg-muted/50"
                  data-testid="button-grwmugc-pb-advanced-settings"
                >
                  {advancedSettingsOpen ? (
                    <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-semibold">Advanced Settings</span>
                      {activeAdvancedOverrideCount > 0 && (
                        <span className="flex items-center gap-2">
                          <span aria-hidden="true" className="text-muted-foreground">·</span>
                          <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                            {activeAdvancedOverrideCount}{" "}
                            {activeAdvancedOverrideCount === 1 ? "override" : "overrides"}
                          </Badge>
                        </span>
                      )}
                    </span>
                    <span className="mt-1 block text-xs font-normal leading-relaxed text-muted-foreground">
                      Optional controls for a specific creative. Leave these unchanged to use the PapaBear campaign defaults.
                    </span>
                  </span>
                </Button>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="mt-4 space-y-5" data-testid="panel-grwmugc-pb-advanced-settings">
                  <div className="grid gap-5 md:grid-cols-2">
                    <div className="min-w-0 space-y-2">
                      <Label htmlFor="grwmugc-pb-occasion" className="text-sm font-medium">
                        GRWM Occasion
                      </Label>
                      <Select value={grwmOccasion} onValueChange={setGrwmOccasion}>
                        <SelectTrigger id="grwmugc-pb-occasion" data-testid="select-grwmugc-pb-occasion" aria-label="GRWM Occasion" className="h-10 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {GRWM_OCCASION_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        The default is a morning post-shower routine. Select another occasion only when testing a different GRWM context.
                      </p>
                      {grwmOccasion === "Custom" && (
                        <div className="space-y-2 pt-1">
                          <Label htmlFor="grwmugc-pb-custom-occasion" className="text-sm font-medium">
                            Custom GRWM Occasion
                          </Label>
                          <Input
                            id="grwmugc-pb-custom-occasion"
                            value={customGrwmOccasion}
                            onChange={(event) => setCustomGrwmOccasion(event.target.value)}
                            placeholder="e.g. Getting ready after an evening swim"
                            data-testid="input-grwmugc-pb-custom-occasion"
                          />
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 space-y-2">
                      <Label htmlFor="grwmugc-pb-creative-twist" className="text-sm font-medium">
                        Creative Twist
                      </Label>
                      <Select value={creativeTwist} onValueChange={setCreativeTwist}>
                        <SelectTrigger id="grwmugc-pb-creative-twist" data-testid="select-grwmugc-pb-creative-twist" aria-label="Creative Twist" className="h-10 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CREATIVE_TWIST_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        A creative twist is optional. When set to None, the prompt agent must not invent an unusual theme or time of day.
                      </p>
                      {creativeTwist === "Custom" && (
                        <div className="space-y-2 pt-1">
                          <Label htmlFor="grwmugc-pb-custom-twist" className="text-sm font-medium">
                            Describe the Creative Twist
                          </Label>
                          <Input
                            id="grwmugc-pb-custom-twist"
                            value={customCreativeTwist}
                            onChange={(event) => setCustomCreativeTwist(event.target.value)}
                            placeholder="e.g. Getting ready while hosting a friend"
                            data-testid="input-grwmugc-pb-custom-twist"
                          />
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 space-y-2">
                      <Label htmlFor="grwmugc-pb-specific-setting" className="text-sm font-medium">
                        Specific Setting
                      </Label>
                      <Input
                        id="grwmugc-pb-specific-setting"
                        value={specificSetting}
                        onChange={(event) => setSpecificSetting(event.target.value)}
                        placeholder="Example: Bright bathroom vanity with natural morning light"
                        data-testid="input-grwmugc-pb-specific-setting"
                      />
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        Leave blank to let the agent choose an appropriate GRWM setting. The setting must still be compatible with the selected GRWM occasion.
                      </p>
                    </div>

                    <div className="min-w-0 space-y-2">
                      <Label htmlFor="grwmugc-pb-opening-action" className="text-sm font-medium">
                        Opening Action
                      </Label>
                      <Input
                        id="grwmugc-pb-opening-action"
                        value={openingAction}
                        onChange={(event) => setOpeningAction(event.target.value)}
                        placeholder="Example: She casually rolls up her robe sleeves"
                        data-testid="input-grwmugc-pb-opening-action"
                      />
                      <div className="space-y-1 text-xs leading-relaxed text-muted-foreground">
                        <p>Leave blank to let the agent generate a new visual hook.</p>
                        <p>The opening action must not involve:</p>
                        <ul className="list-disc space-y-0.5 pl-4">
                          <li>Setting down a phone</li>
                          <li>Adjusting a phone</li>
                          <li>Mounting a phone</li>
                          <li>Touching visible recording equipment</li>
                          <li>Speaking or mouthing words to the camera</li>
                        </ul>
                      </div>
                    </div>

                    <div className="min-w-0 space-y-2">
                      <Label htmlFor="grwmugc-pb-application-order" className="text-sm font-medium">
                        Application Order
                      </Label>
                      <Input
                        id="grwmugc-pb-application-order"
                        value={applicationOrder}
                        onChange={(event) => setApplicationOrder(event.target.value)}
                        placeholder="Example: Neck, forearm, elbow, then hands"
                        data-testid="input-grwmugc-pb-application-order"
                      />
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        Leave blank to use the product’s default application profile. The agent must only use body areas appropriate for the selected product; the override cannot force an inappropriate product application.
                      </p>
                    </div>

                    <div className="min-w-0 space-y-2">
                      <Label htmlFor="grwmugc-pb-closing-activity" className="text-sm font-medium">
                        Closing Activity
                      </Label>
                      <Input
                        id="grwmugc-pb-closing-activity"
                        value={closingActivity}
                        onChange={(event) => setClosingActivity(event.target.value)}
                        placeholder="Example: She slips on a bracelet and walks out of frame"
                        data-testid="input-grwmugc-pb-closing-activity"
                      />
                      <div className="space-y-1 text-xs leading-relaxed text-muted-foreground">
                        <p>Leave blank to let the agent generate a natural, active GRWM ending.</p>
                        <p>The closing should not include:</p>
                        <ul className="list-disc space-y-0.5 pl-4">
                          <li>A spoken call to action</li>
                          <li>Pointing at the product</li>
                          <li>Holding the product toward the camera</li>
                          <li>A sales presentation</li>
                          <li>A closing caption</li>
                          <li>A deadpan pose with no activity</li>
                        </ul>
                      </div>
                    </div>

                    <div className="min-w-0 space-y-2 md:col-span-2">
                      <Label htmlFor="grwmugc-pb-concepts-to-avoid" className="text-sm font-medium">
                        Concepts to Avoid
                      </Label>
                      <Textarea
                        id="grwmugc-pb-concepts-to-avoid"
                        value={conceptsToAvoid}
                        onChange={(event) => setConceptsToAvoid(event.target.value)}
                        placeholder="Example: Do not repeat the mug-opening hook or the jewelry ending"
                        rows={3}
                        data-testid="textarea-grwmugc-pb-concepts-to-avoid"
                        className="resize-y"
                      />
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        Creative history should be checked automatically. Use this field only to add specific concepts that the user does not want repeated.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end border-t border-dashed border-border/60 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetAdvancedSettings}
                      data-testid="button-grwmugc-pb-reset-advanced-settings"
                    >
                      <RotateCcw className="h-4 w-4" aria-hidden="true" />
                      Reset to Defaults
                    </Button>
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {Object.keys(validationErrors).length > 0 && (
            <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              Complete the highlighted fields before continuing.
            </div>
          )}
          {isReviewed && (
            <div role="status" className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-emerald-700 dark:text-emerald-300">
              <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Setup is ready and still stored only in this page. Video generation has not started.</span>
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              {selectedPillar && selectedPersona && selectedCharacter && selectedProduct
                ? `${selectedPillar.name} · ${selectedPersona.name} · ${selectedCharacter.name} · ${selectedProduct.name}`
                : "Select all required inputs to review this setup."}
            </p>
            <Button type="button" onClick={validate} data-testid="button-grwmugc-pb-review">
              <Check className="mr-2 h-4 w-4" aria-hidden="true" />
              Review setup
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}